import { paymentOrderService, type PaymentOrder } from './paymentOrderService'
import api from './api'
import apiClient from './axios'
import type { Order, OrderSearchParams } from '@/types/order.types'

function normalizeBinary(value: unknown): 0 | 1 {
  return value === 1 || value === '1' || value === true || value === 'true' ? 1 : 0
}

function normalizeBoolean(value: unknown, fallback: boolean = true): boolean {
  if (value == null) return fallback
  if (value === true || value === 'true' || value === 1 || value === '1') return true
  if (value === false || value === 'false' || value === 0 || value === '0') return false
  return fallback
}

function normalizeFromAppValue(value: unknown): 0 | 1 | 2 | 3 {
  if (value === 2 || value === '2') return 2
  if (value === 3 || value === '3') return 3
  if (value === true || value === 'true' || value === 1 || value === '1') return 1
  return 0
}

function firstNonEmptyString(...values: unknown[]): string | null {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value.trim()
    if (typeof value === 'number' && Number.isFinite(value)) return String(value)
  }
  return null
}

function normalizeNullableString(value: unknown): string | null {
  const normalized = firstNonEmptyString(value)
  return normalized || null
}

function getTranslationName(value: unknown): string | null {
  if (!Array.isArray(value)) return null

  for (const item of value) {
    if (!item || typeof item !== 'object') continue
    const name = firstNonEmptyString(
      (item as Record<string, unknown>).name,
      (item as Record<string, unknown>).title,
      (item as Record<string, unknown>).label
    )
    if (name) return name
  }

  return null
}

function extractNamedEntityLabel(value: unknown): string | null {
  if (!value || typeof value !== 'object') return null

  const record = value as Record<string, unknown>

  return firstNonEmptyString(
    record.name,
    record.title,
    record.label,
    getTranslationName(record.translations),
    extractNamedEntityLabel(record.vip),
    extractNamedEntityLabel(record.resource),
    extractNamedEntityLabel(record.item),
    extractNamedEntityLabel(record.product),
    extractNamedEntityLabel(record.biz),
    extractNamedEntityLabel(record.detail)
  )
}

function extractBoughtItemLabel(item: unknown): string | null {
  if (!item || typeof item !== 'object') return null

  const record = item as Record<string, unknown>

  return firstNonEmptyString(
    extractNamedEntityLabel(record),
    extractNamedEntityLabel(record.data),
    extractNamedEntityLabel(record.extra),
    extractNamedEntityLabel(record.meta)
  )
}

// API Data Mapping
export function mapPaymentOrderToOrder(paymentOrder: PaymentOrder): Order {
  const rawPaymentOrder = paymentOrder as Record<string, any>
  const rawAgent = paymentOrder.agent ?? rawPaymentOrder.agent ?? null
  const rawUser = paymentOrder.user ?? rawPaymentOrder.user ?? null
  const rawPlatform = paymentOrder.platform ?? rawPaymentOrder.platform ?? null
  const rawChannel = paymentOrder.channel ?? rawPaymentOrder.channel ?? null
  const orderNo =
    firstNonEmptyString(
      paymentOrder.orderNo,
      rawPaymentOrder.order_no,
      rawPaymentOrder.orderNumber,
      rawPaymentOrder.order_number,
      paymentOrder.paymentPlatformOrderNo,
      rawPaymentOrder.payment_platform_order_no,
      paymentOrder.id,
      rawPaymentOrder.id
    ) || ''
  const createdAt =
    firstNonEmptyString(
      paymentOrder.createdAt,
      rawPaymentOrder.created_at,
      rawPaymentOrder.created_at_utc,
      rawPaymentOrder.createTime,
      rawPaymentOrder.created_time
    ) || ''

  // Get product name from userBoughts if available
  let productName = 'Unknown'
  const vipTranslationName =
    String(paymentOrder.bizType ?? rawPaymentOrder.biz_type).toLowerCase() === 'vip'
      ? getTranslationName((paymentOrder.vip ?? rawPaymentOrder.vip)?.translations)
      : null

  if (vipTranslationName) {
    productName = vipTranslationName
  } else if (paymentOrder.userBoughts && paymentOrder.userBoughts.length > 0) {
    const itemLabels = paymentOrder.userBoughts
      .map(extractBoughtItemLabel)
      .filter((label): label is string => Boolean(label))

    if (itemLabels.length > 0) {
      productName = itemLabels.join(', ')
    } else {
      const types = paymentOrder.userBoughts
        .map((item) => {
          if (!item || typeof item !== 'object') return null
          const record = item as Record<string, unknown>
          return firstNonEmptyString(record.type)
        })
        .filter((label): label is string => Boolean(label))

      productName = types.join(', ') || 'Unknown'
    }
  } else {
    // Fallback to productName field from API response
    productName = paymentOrder.productName || rawPaymentOrder.product_name || paymentOrder.bizType || rawPaymentOrder.biz_type || 'Unknown'
  }

  // Handle API response structure
  const agentName = firstNonEmptyString(rawAgent?.username, rawAgent?.name, rawAgent?.loginId, rawAgent?.login_id) || 'Unknown'
  const userId = firstNonEmptyString(paymentOrder.userId, rawPaymentOrder.user_id, rawUser?.id, rawUser?.display_id) || 'Unknown'
  const userUsername =
    firstNonEmptyString(
      rawUser?.username,
      rawUser?.nickname
    ) || 'Unknown'
  const orderStatus = String(paymentOrder.status ?? rawPaymentOrder.status ?? 'pending') as 'pending' | 'success' | 'failed' | 'closed' | 'refunded'
  const paymentChannel = rawChannel?.name || rawPlatform?.name || 'Unknown'
  const orderType = String(paymentOrder.bizType ?? rawPaymentOrder.biz_type ?? 'resource') as 'Video' | 'Community' | 'VIP' | 'resource'
  const rawStolen = paymentOrder.isStolen ?? paymentOrder.is_stolen
  const isStolen: 0 | 1 = normalizeBinary(rawStolen)

  const rawLock = paymentOrder.lockOrder ?? paymentOrder.lock_order
  const lockOrder: 0 | 1 = normalizeBinary(rawLock)
  const rawAgentActive = rawAgent?.is_active ?? rawAgent?.active
  const agentIsActive = normalizeBoolean(rawAgentActive, true)
  const rawUserActive = rawUser?.is_active ?? rawUser?.active
  // Orders API semantics in this project: true means blocked user, false means active user.
  const userIsActive = normalizeBoolean(rawUserActive, false)
  const hasUserRecord = Boolean(rawUser)
  const agentRole = typeof rawAgent?.role === 'string' ? rawAgent.role : null
  const agentId = normalizeNullableString(paymentOrder.agentId ?? rawPaymentOrder.agent_id ?? rawAgent?.id)

  return {
    id: paymentOrder.id != null && paymentOrder.id !== '' ? String(paymentOrder.id) : '',
    orderNumber: orderNo,
    agentName: agentName,
    userId: userId,
    userUsername,
    productName: productName,
    orderAmount: parseFloat(String(paymentOrder.amount ?? rawPaymentOrder.amount ?? 0)),
    orderStatus: orderStatus,
    isStolen,
    hasDeduction: isStolen,
    lockOrder,
    agentIsActive,
    userIsActive,
    hasUserRecord,
    paymentChannel: paymentChannel,
    orderType: orderType,
    userIp: paymentOrder.ipAddress || rawPaymentOrder.ip_address || 'N/A',
    from_app: normalizeFromAppValue(paymentOrder.from_app ?? paymentOrder.fromApp),
    browser: paymentOrder.browser || rawPaymentOrder.browser || null,
    // Keep raw API timestamps and format once in the UI for consistent timezone handling.
    regTime: rawUser?.created_at || rawUser?.createdAt || '',
    orderTime: createdAt,
    _orderDate: createdAt ? new Date(createdAt) : new Date(0),
    // Additional fields from API
    agentId,
    agentRole,
    bizType: paymentOrder.bizType ?? rawPaymentOrder.biz_type,
    bizId: paymentOrder.bizId ?? rawPaymentOrder.biz_id,
    currency: paymentOrder.currency,
    basePriceCny: paymentOrder.basePriceCny,
    paymentPlatformId: paymentOrder.paymentPlatformId ?? rawPaymentOrder.payment_platform_id,
    paymentChannelId: paymentOrder.paymentChannelId ?? rawPaymentOrder.payment_channel_id,
    paymentPlatformOrderNo: paymentOrder.paymentPlatformOrderNo ?? rawPaymentOrder.payment_platform_order_no,
    paymentMethod: paymentOrder.paymentMethod ?? rawPaymentOrder.payment_method,
    paidAt: paymentOrder.paidAt ?? rawPaymentOrder.paid_at,
    expiredAt: paymentOrder.expiredAt ?? rawPaymentOrder.expired_at,
    notifyCount: paymentOrder.notifyCount,
    lastNotifyAt: paymentOrder.lastNotifyAt ?? rawPaymentOrder.last_notify_at,
    extra: paymentOrder.extra,
    deletedAt: paymentOrder.deletedAt ?? rawPaymentOrder.deleted_at,
    platform: rawPlatform,
    channel: rawChannel
  }
}

// API Functions
export async function fetchPaymentOrders(page: number = 1, pageSize: number = 20, searchParams?: OrderSearchParams) {
  try {
    // Build request parameters including active filters
    const requestParams: any = {
      page,
      pageSize,
      ...(searchParams ?? {}),
    }
    
    const response = await paymentOrderService.findAllPaymentOrders(requestParams)
    
    const mappedOrders = response.items.map(mapPaymentOrderToOrder)
    
    return {
      orders: mappedOrders,
      total: response.total,
      totalPages: response.totalPages,
      hasNextPage: response.hasNextPage,
      hasPreviousPage: response.hasPreviousPage,
      page: response.page,
      pageSize: response.pageSize
    }
  } catch (err) {
    console.error('Error fetching payment orders:', err)
    throw new Error('Failed to fetch payment orders')
  }
}

export async function searchPaymentOrders(searchParams: any) {
  try {
    const response = await apiClient.post(api.searchPayementChannels(), searchParams)
    
    const mappedOrders = response.data.items.map(mapPaymentOrderToOrder)
    
    return {
      orders: mappedOrders,
      total: response.data.total,
      totalPages: response.data.totalPages,
      hasNextPage: response.data.hasNextPage,
      hasPreviousPage: response.data.hasPreviousPage,
      page: response.data.page,
      pageSize: response.data.pageSize
    }
  } catch (err) {
    console.error('Error searching payment orders:', err)
    throw new Error('Failed to search payment orders')
  }
}

export async function fetchAdmins() {
  try {
    const response = await apiClient.post(api.listAdmins())
    // Extract usernames from admin data
    const agents = response.data.map((admin: any) => (admin.username || admin.name) as string).filter(Boolean)
    return [...new Set(agents as string[])].sort()
  } catch (error) {
    console.error('Error fetching admins:', error)
    throw error
  }
}

export async function fetchPaymentChannels() {
  try {
    const response = await apiClient.post(api.paySelect())
    // Return full channel objects with id, name, and channelCode
    const channels = response.data.data.map((channel: any) => ({
      id: channel.id,
      name: channel.name,
      channelCode: channel.channelCode,
      code: channel.code
    }))
    return channels
  } catch (error) {
    console.error('Error fetching payment channels:', error)
    throw error
  }
}

export async function lockPaymentOrderById(paymentOrderId: string): Promise<void> {
  await apiClient.post(api.lockPaymentOrder(), { id: paymentOrderId })
}

export async function cancelStealPaymentOrderById(paymentOrderId: string): Promise<void> {
  await apiClient.post(api.cancelStealPaymentOrder(), { id: paymentOrderId })
}
